import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { AccountCircle, Info } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { isAuthenticated, logout } from '../../services/authService';
import DisclaimerModal from './DisclaimerModal';
import useDisclaimer from '../../hooks/useDisclaimer';

const Navbar: React.FC = () => {
    const [authenticated, setAuthenticated] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
    const { isOpen: disclaimerOpen, openDisclaimer, closeDisclaimer } = useDisclaimer();
    const navigate = useNavigate();
    const location = useLocation();

    // Listen for auth events - no initial check, wait for auth:initialized
    useEffect(() => {
        const checkAuth = () => {
            setAuthenticated(isAuthenticated());
            setAuthLoading(false);
        };

        // Listen for custom auth events
        const handleAuthLogin = () => {
            setAuthenticated(true);
            setAuthLoading(false);
        };

        const handleAuthLogout = () => {
            setAuthenticated(false);
            setAuthLoading(false);
        };

        // Listen for storage changes (for logout in other tabs)
        const handleStorageChange = () => {
            checkAuth();
        };

        // Listen for auth initialization completion
        const handleAuthInitialized = () => {
            checkAuth();
        };

        // Add event listeners
        window.addEventListener('auth:login', handleAuthLogin);
        window.addEventListener('auth:logout', handleAuthLogout);
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('auth:initialized', handleAuthInitialized);

        // Set up interval to check authentication status periodically
        //const interval = setInterval(checkAuth, 30000); // Reduced frequency since we have event listeners

        return () => {
            //clearInterval(interval);
            window.removeEventListener('auth:login', handleAuthLogin);
            window.removeEventListener('auth:logout', handleAuthLogout);
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('auth:initialized', handleAuthInitialized);
        };
    }, []);

    const handleLogout = () => {
        logout();
        setAuthenticated(false);
        setProfileMenuAnchor(null);
        navigate('/login');
    };

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setProfileMenuAnchor(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setProfileMenuAnchor(null);
    };

    const isActivePath = (path: string) => {
        if (path === '/') {
            return false; // Never show active style for home page
        }
        return location.pathname === path;
    };


    return (
        <Box sx={{ flexGrow: 1, mb: 2 }}>
            <AppBar position="static" color="default" elevation={1}>
                <Toolbar>
                    <Typography 
                        variant="h6" 
                        component={RouterLink} 
                        to="/" 
                        sx={{ 
                            flexGrow: 1, 
                            textDecoration: 'none', 
                            color: 'inherit',
                            fontWeight: isActivePath('/') ? 'bold' : 'normal',
                            borderBottom: isActivePath('/') ? 2 : 0,
                            borderColor: 'primary.main'
                        }}
                    >
                        EthicsVC&nbsp;<img src="/assets/ethics-logo.png" alt="" style={{ height: 28, marginLeft: 0 }} />
                    </Typography>
                    {!authLoading && authenticated && (
                        <>
                            <Button 
                                color="inherit" 
                                component={RouterLink} 
                                to="/about-us"
                                sx={{
                                    fontWeight: isActivePath('/about-us') ? 'bold' : 'normal',
                                    borderBottom: isActivePath('/about-us') ? 2 : 0,
                                    borderColor: 'primary.main',
                                    borderRadius: 0
                                }}
                            >
                                About Us
                            </Button>
                            <Button color="inherit" disabled>Submission</Button>
                            <Button color="inherit" disabled>Review</Button>
                            <Button color="inherit" disabled>Contact</Button>
                            <IconButton
                                color="inherit"
                                onClick={handleProfileMenuOpen}
                                sx={{ ml: 1 }}
                            >
                                <AccountCircle />
                            </IconButton>
                            <Menu
                                anchorEl={profileMenuAnchor}
                                open={Boolean(profileMenuAnchor)}
                                onClose={handleProfileMenuClose}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            >
                                <MenuItem 
                                    component={RouterLink} 
                                    to="/account" 
                                    onClick={handleProfileMenuClose}
                                    sx={{
                                        fontWeight: isActivePath('/account') ? 'bold' : 'normal'
                                    }}
                                >
                                    Account
                                </MenuItem>
                                <MenuItem onClick={handleLogout}>
                                    Logout
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                    <Button 
                        color="inherit" 
                        onClick={openDisclaimer}
                        sx={{ 
                            backgroundColor: 'info.main',
                            color: 'info.contrastText',
                            fontWeight: 'bold',
                            '&:hover': {
                                backgroundColor: 'info.dark'
                            }
                        }}
                    >
                        Disclaimer
                    </Button>
                    {!authLoading && !authenticated && (
                        <Button color="inherit" variant="outlined" sx={{ ml: 2 }} component={RouterLink} to="/login">
                            Login
                        </Button>
                    )}
                </Toolbar>
            </AppBar>
            <DisclaimerModal 
                open={disclaimerOpen} 
                onClose={closeDisclaimer} 
            />
        </Box>
    );
};

export default Navbar;
